import { CommonModule } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { AddKeywordsScanComponent } from '../../common/add-keywords-scan/add-keywords-scan.component';
import { selectKeywords } from '../../common/interfaces/keyword-list/base-keyword-list.interface';
import { ConversationKeywordSuggestion } from '../../services/keywords.service';
import { ScanFromChatService } from '../scan-from-chat.service';

/**
 * In-chat keyword-review surface for "Scan this document" (Phase 2).
 * Hosts the shared <app-add-keywords-scan> picker (existing library, pre-selected by M1)
 * and a panel of NEW keyword suggestions (M2b). On confirm it asks ScanFromChatService to
 * create the opted-in suggestions and start a document scan from the attachment.
 */
@Component({
  selector: 'app-scan-from-chat-dialog',
  imports: [CommonModule, DialogModule, ButtonModule, AddKeywordsScanComponent],
  templateUrl: './scan-from-chat-dialog.component.html',
})
export class ScanFromChatDialogComponent {
  protected readonly scanService = inject(ScanFromChatService);
  protected selectedKeywords: selectKeywords[] = [];
  protected readonly selectedSuggestions = new Set<string>();

  constructor() {
    // Reset local selection whenever the dialog (re)opens so a previous attachment's
    // picks don't leak into the next scan. M1 seeds `selectedKeywords` from inference.
    effect(() => {
      if (this.scanService.dialogVisible()) {
        this.selectedKeywords = [...this.scanService.initialKeywords()];
        this.selectedSuggestions.clear();
      }
    });
  }

  get hasSelection(): boolean {
    return this.selectedKeywords.length > 0 || this.selectedSuggestions.size > 0;
  }

  onSelectionChanged(keywords: selectKeywords[]): void {
    this.selectedKeywords = keywords;
  }

  isSuggestionSelected(suggestion: ConversationKeywordSuggestion): boolean {
    return this.selectedSuggestions.has(suggestion.name);
  }

  toggleSuggestion(suggestion: ConversationKeywordSuggestion): void {
    if (this.selectedSuggestions.has(suggestion.name)) {
      this.selectedSuggestions.delete(suggestion.name);
    } else {
      this.selectedSuggestions.add(suggestion.name);
    }
  }

  start(): void {
    const accepted = this.scanService
      .suggestions()
      .filter((suggestion) => this.selectedSuggestions.has(suggestion.name));
    void this.scanService.startScan(this.selectedKeywords, accepted);
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) {
      this.scanService.close();
    }
  }
}
